// KPI içe aktarma — `/api/v1/kpi/*` import/evidence sarmalayıcıları (M16 T11).
// `api/kpi.ts`'e dokunulmaz (eşzamanlı düzenleniyor). Zod doğrulama hataları (422
// `{error:"validation", issues}`) `apiRaw` ile yakalanır ve `issues` konum bilgisiyle
// `KpiImportRequestError`'a taşınır — UI mesajları aynen gösterir.

import type {
  EvidenceDocument,
  KpiImportColumn,
  KpiImportGrid,
  KpiImportJobStatus,
  KpiMappingDefaults,
  KpiMappingRow,
  KpiResolvedGrid,
  KpiSetIndicator,
  KpiValueStatus,
} from "@mahir/shared";
import { ApiError, api, apiRaw, uploadFileWithProgress } from "./client";
import { getKpiSet, listKpiMetrics } from "./kpi";
import { getKpiEvidence } from "./kpi-evidence";

export interface KpiImportJobDto {
  id: string;
  clientId: string;
  setId: string;
  fileName: string;
  fileType: string;
  evidenceId: string | null;
  status: KpiImportJobStatus;
  stage: string | null;
  errorDetail: string | null;
  attempts: number;
  grid: KpiImportGrid | null;
  mapping: KpiMappingRow[] | null;
  /** B-02/B-03 — taslakta saklanan varsayılanlar (yıl/işletme kolonu olmayan dosya). */
  defaultYear: number | null;
  defaultEntityId: string | null;
  /** P2-6 — sayfa bazlı yıl (grid dizi indeksi → yıl). */
  defaultYearBySheet: Record<string, number>;
  /** P2-8 — plana girmeyen sayfalar (grid dizi indeksi). */
  skippedSheets: number[];
  /** P1-1 — öneri aşamasının uyarıları ("Parça 2: 83/84 satır için model öneri vermedi"). */
  mappingWarnings: string[];
  createdBy: string | null;
  createdByKind: string;
  createdAt: string;
  updatedAt: string;
}

export type KpiPlanOutcome = "create" | "update" | "conflict" | "skip_derived" | "unchanged";

export interface KpiPlannedCell {
  rowId: string;
  metricId: string;
  code: string;
  isDerived: boolean;
  entityId: string;
  year: number;
  status: KpiValueStatus;
  numericValue?: number;
  textValue?: string;
  locator: { sheet: string; cell: string };
  action: "map" | "create" | "reported";
  existing?: { status: string; value: number | null; text: string | null; version: number };
  outcome: KpiPlanOutcome;
  /** P1-3 — dosya birimi hedef birime çevrildi (numericValue = original × factor). */
  converted?: { fromUnit: string; toUnit: string; factor: number; original: number };
  /** R10 — birim karşılaştırılamadı: değer olduğu gibi yazılır (önizlemede gri not). */
  unitNote?: { file: string; target: string };
}

export interface KpiImportSkippedRow {
  rowId: string;
  reason: string;
  /** P1-2 — müşteri yolunda erişim/C3 nedeniyle düşen satır. */
  access?: "readonly" | "closed" | "create";
  /** DR7 — birim uyumsuzluğu (yazılmayacak satır). */
  unit?: { kind: "incompatible"; file: string; target: string };
}

/** spec §7.2 — planın kırılım satırı (sunucu `PlannedBreakdown` ile elle eşlenik). */
export interface KpiImportPlannedBreakdown {
  targetKey: string;
  parentCode: string;
  parentMetricId: string;
  dimension: string;
  groupLabel: string;
  groupExists: boolean;
  groupMetricId: string | null;
  groupInSet: boolean;
  complete: boolean;
  rollup: "sum" | "avg" | "none";
  member: string;
  memberLabel: string;
  memberExists: boolean;
  memberMetricId: string | null;
  memberCode: string | null;
  memberInSet: boolean;
}

export interface KpiImportPlan {
  cells: KpiPlannedCell[];
  skipped: KpiImportSkippedRow[];
  /** Özet uyarılar (tek satır). */
  warnings: string[];
  /** P2-3 — aynı hedefe düşen hücrelerin tek tek listesi (katlanabilir). */
  dedupeDetails: string[];
  newIndicatorCodes: string[];
  /** spec §7.2 — kırılım olarak eklenecek satırlar (eski sunucu yanıtında alan yok). */
  newBreakdowns?: KpiImportPlannedBreakdown[];
  unresolvedEntities: string[];
  /** B-02/B-03 — seçim yapılmadan yazılamayan satırlar var (Adım 2 zorunlu seçici). */
  missing: ("year" | "entity")[];
  /** P2-6 — yıl seçimi bekleyen sayfalar (grid dizi indeksi). */
  missingYearSheets: number[];
  /** P2-8 — plana girmeyen sayfalar. */
  skippedSheets: number[];
  /** P1-3 — birim çevrimi uygulanan hücre sayısı. */
  convertedCells: number;
  /** TODO-KPI-37 — sayfa adı → sayı yazım biçimi oyu ("tr" | "en" | "mixed"). */
  sheetLocales?: Record<string, "tr" | "en" | "mixed">;
  /** TODO-KPI-37 — karışık yazımlı sayfada tek gruplu "1,234"/"1.234" (TR okundu). */
  localeWarnings?: {
    code: "number_locale_ambiguous";
    sheet: string;
    rowId: string;
    col: string;
    raw: string;
    value: number;
  }[];
}

export interface KpiImportPreview {
  plan: KpiImportPlan;
  recalculated: KpiResolvedGrid;
}

export interface KpiImportCommitResult {
  created: number;
  updated: number;
  skipped: number;
  newIndicators: number;
  /** spec §7.2 — kırılım üyesi olarak sete eklenen satır. */
  newBreakdownMembers?: number;
  conflictsKept: number;
  /** P1-2 — müşteri yolunda erişim nedeniyle düşen satırlar. */
  accessSkipped: { rowId: string; rowLabel: string; reason: string }[];
}

export type KpiConflictPolicy = "overwrite" | "keep_existing";

export interface ZodIssueLike {
  path: (string | number)[];
  message: string;
}

/** 422 doğrulama hatası — `issues` konum bilgili Zod mesajları. */
export class KpiImportRequestError extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    public issues: ZodIssueLike[] = [],
    details?: unknown,
  ) {
    super(status, code, message, details);
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  issues?: unknown;
  details?: unknown;
}

function toIssues(raw: unknown): ZodIssueLike[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((i): ZodIssueLike | null => {
      if (!i || typeof i !== "object") return null;
      const o = i as { path?: unknown; message?: unknown };
      return {
        path: Array.isArray(o.path)
          ? o.path.filter(
              (p): p is string | number => typeof p === "string" || typeof p === "number",
            )
          : [],
        message: typeof o.message === "string" ? o.message : "geçersiz değer",
      };
    })
    .filter((i): i is ZodIssueLike => i !== null);
}

async function request<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown } = {},
): Promise<T> {
  const res = await apiRaw<T | ErrorBody>(path, opts);
  if (res.ok) return res.body as T;
  const b: ErrorBody = (res.body as ErrorBody | null) ?? {};
  const issues = toIssues(b.issues);
  const message =
    b.message ??
    (issues.length > 0 ? "Gönderilen veri doğrulanamadı" : `HTTP ${res.status.toString()}`);
  throw new KpiImportRequestError(res.status, b.error ?? "unknown", message, issues, b.details);
}

// ─── Kaynak dosya (kanıt belgesi, sourceType 'import') ──────────────────────

const MIME_BY_KIND = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
} as const;
export type KpiImportFileKind = keyof typeof MIME_BY_KIND;

export function kpiImportMime(kind: KpiImportFileKind): string {
  return MIME_BY_KIND[kind];
}

export async function presignKpiEvidence(body: {
  clientId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sourceType: "upload" | "import" | "manual";
}): Promise<{ evidence: EvidenceDocument; uploadUrl: string; expiresIn: number }> {
  return request("/kpi/evidence/presign", { method: "POST", body });
}

export async function commitKpiEvidence(id: string): Promise<EvidenceDocument> {
  const res = await request<{ evidence: EvidenceDocument }>(`/kpi/evidence/${id}/commit`, {
    method: "POST",
    body: {},
  });
  return res.evidence;
}

/** presign → PUT (ilerleme) → commit; dönen kanıt kimliği `createKpiImportJob`'a verilir. */
export async function uploadKpiImportFile(
  clientId: string,
  file: File,
  kind: KpiImportFileKind,
  onProgress?: (pct: number) => void,
): Promise<EvidenceDocument> {
  const presign = await presignKpiEvidence({
    clientId,
    fileName: file.name,
    mimeType: kpiImportMime(kind),
    sizeBytes: file.size,
    sourceType: "import",
  });
  await uploadFileWithProgress(presign.uploadUrl, file, onProgress);
  return commitKpiEvidence(presign.evidence.id);
}

// ─── İşler ──────────────────────────────────────────────────────────────────

export async function createKpiImportJob(
  setId: string,
  evidenceId: string,
): Promise<KpiImportJobDto> {
  const res = await request<{ job: KpiImportJobDto }>(`/kpi/sets/${setId}/import`, {
    method: "POST",
    body: { evidenceId },
  });
  return res.job;
}

export async function getKpiImportJob(jobId: string): Promise<KpiImportJobDto> {
  const res = await api<{ job: KpiImportJobDto }>(`/kpi/import-jobs/${jobId}`);
  return res.job;
}

/** Taslak işler (committed hariç; `grid` taşınmaz). */
export async function listKpiImportJobs(setId: string): Promise<KpiImportJobDto[]> {
  const res = await api<{ jobs: KpiImportJobDto[] }>(`/kpi/sets/${setId}/import-jobs`);
  return res.jobs;
}

/** Gövdeye yalnız tanımlı varsayılanlar girer (Zod `optional`). */
export function defaultsBody(defaults: KpiMappingDefaults | undefined): KpiMappingDefaults {
  if (!defaults) return {};
  return {
    ...(defaults.defaultYear !== undefined ? { defaultYear: defaults.defaultYear } : {}),
    ...(defaults.defaultEntityId !== undefined
      ? { defaultEntityId: defaults.defaultEntityId }
      : {}),
    ...(defaults.defaultYearBySheet !== undefined
      ? { defaultYearBySheet: defaults.defaultYearBySheet }
      : {}),
    ...(defaults.skippedSheets !== undefined ? { skippedSheets: defaults.skippedSheets } : {}),
  };
}

export async function saveKpiImportMapping(
  jobId: string,
  mapping: KpiMappingRow[],
  columns?: KpiImportColumn[][],
  defaults?: KpiMappingDefaults,
): Promise<KpiImportJobDto> {
  const res = await request<{ job: KpiImportJobDto }>(`/kpi/import-jobs/${jobId}/mapping`, {
    method: "PUT",
    body: { mapping, ...(columns ? { columns } : {}), ...defaultsBody(defaults) },
  });
  return res.job;
}

export async function deleteKpiImportJob(jobId: string): Promise<void> {
  await request(`/kpi/import-jobs/${jobId}`, { method: "DELETE" });
}

export async function previewKpiImport(
  jobId: string,
  mapping: KpiMappingRow[],
  defaults?: KpiMappingDefaults,
): Promise<KpiImportPreview> {
  return request(`/kpi/import-jobs/${jobId}/preview`, {
    method: "POST",
    body: { mapping, ...defaultsBody(defaults) },
  });
}

export async function commitKpiImport(
  jobId: string,
  mapping: KpiMappingRow[],
  conflictPolicy: KpiConflictPolicy,
  defaults?: KpiMappingDefaults,
): Promise<KpiImportCommitResult> {
  return request(`/kpi/import-jobs/${jobId}/commit`, {
    method: "POST",
    body: { mapping, conflictPolicy, ...defaultsBody(defaults) },
  });
}

export async function retryKpiImport(
  jobId: string,
  opts: { skipMapping?: boolean } = {},
): Promise<KpiImportJobDto> {
  const res = await request<{ job: KpiImportJobDto }>(`/kpi/import-jobs/${jobId}/retry`, {
    method: "POST",
    body: opts.skipMapping ? { skipMapping: true } : {},
  });
  return res.job;
}

// ─── API tabanı (T19 — aynı sihirbaz, iç `/kpi` ya da portal `/portal/kpi`) ─────
// ImportWizard ve adımları bu arayüz üzerinden konuşur; varsayılan iç API. Portal uygulaması
// `api/portal-kpi.ts`'te (presign set-bazlı, katalog/işletme uçları yok → sihirbaz `isClient`).

export interface ImportTargetOptionDto {
  code: string;
  label: string;
  unit: string | null;
  isDerived: boolean;
  /** Çocuğu olan düğüm — hedef olamaz (P2-9); listeye girmez. */
  isCategory?: boolean;
}

export interface ImportApi {
  uploadFile: (
    ctx: { clientId: string; setId: string },
    file: File,
    kind: KpiImportFileKind,
    onProgress?: (pct: number) => void,
  ) => Promise<EvidenceDocument>;
  createJob: (setId: string, evidenceId: string) => Promise<KpiImportJobDto>;
  /**
   * 2026-10-08 — kaynak dosyanın tarama durumu: iş yalnız `scanStatus === "clean"` dosyada
   * açılır; yükleme adımı commit'ten sonra bunu yoklar (lib/kpi-import-scan `waitForCleanScan`).
   */
  getEvidence: (evidenceId: string) => Promise<Pick<EvidenceDocument, "id" | "scanStatus">>;
  getJob: (jobId: string) => Promise<KpiImportJobDto>;
  listJobs: (setId: string) => Promise<KpiImportJobDto[]>;
  saveMapping: (
    jobId: string,
    mapping: KpiMappingRow[],
    columns?: KpiImportColumn[][],
    defaults?: KpiMappingDefaults,
  ) => Promise<KpiImportJobDto>;
  /** Taslak iş silme (aktarılmamış); liste tazelenir. */
  deleteJob: (jobId: string) => Promise<void>;
  preview: (
    jobId: string,
    mapping: KpiMappingRow[],
    defaults?: KpiMappingDefaults,
  ) => Promise<KpiImportPreview>;
  commit: (
    jobId: string,
    mapping: KpiMappingRow[],
    conflictPolicy: KpiConflictPolicy,
    defaults?: KpiMappingDefaults,
  ) => Promise<KpiImportCommitResult>;
  retry: (jobId: string, opts?: { skipMapping?: boolean }) => Promise<KpiImportJobDto>;
  /** Eşleştirme hedefleri: set göstergeleri (+ iç API'de katalog). Portalda katalog boş. */
  loadTargets: (
    setId: string,
    clientId: string,
  ) => Promise<{
    setIndicators: KpiSetIndicator[];
    catalog: ImportTargetOptionDto[];
  }>;
}

export const INTERNAL_IMPORT_API: ImportApi = {
  uploadFile: (ctx, file, kind, onProgress) =>
    uploadKpiImportFile(ctx.clientId, file, kind, onProgress),
  createJob: createKpiImportJob,
  getEvidence: async (evidenceId) => (await getKpiEvidence(evidenceId)).evidence,
  getJob: getKpiImportJob,
  listJobs: listKpiImportJobs,
  saveMapping: saveKpiImportMapping,
  deleteJob: deleteKpiImportJob,
  preview: previewKpiImport,
  commit: commitKpiImport,
  retry: retryKpiImport,
  loadTargets: async (setId, clientId) => {
    const [set, catalog] = await Promise.all([getKpiSet(setId), listKpiMetrics({ clientId })]);
    const inSet = new Set(set.indicators.map((i) => i.code));
    // P2-9 — katalogda çocuğu olan düğüm (kategori) hedef olamaz
    const parents = new Set(
      catalog.metrics.map((mt) => mt.parentId).filter((p): p is string => p !== null),
    );
    return {
      setIndicators: set.indicators,
      catalog: catalog.metrics
        .filter((mt) => !inSet.has(mt.code) && !parents.has(mt.id))
        .map((mt) => ({
          code: mt.code,
          label: mt.label,
          unit: mt.defaultUnit,
          isDerived: mt.isDerived,
        })),
    };
  },
};
